import { motion } from 'framer-motion';
import { Droplets, Briefcase, Heart, Shield, Users, GraduationCap } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Card, CardContent } from '../components/ui/card';

const HERO_BG = '/assets/images/about.png';
const PROGRAM_IMAGE = "https://images.unsplash.com/photo-1637997840862-9aafaf835eed?w=800";

const programs = [
  {
    category: 'Humanitarian Support',
    icon: Heart,
    color: '#2D3E55',
    initiative: 'RAHVAI',
    description: 'Through RAHVAI, humanitarian outreach initiatives support vulnerable communities.',
    items: [
      'Food support programs',
      'Community assistance outreach',
      'Clean water initiatives',
      'Community development programs',
    ],
    goal: 'Restore dignity and strengthen resilience among underserved populations.',
  },
  {
    category: 'Economic Empowerment',
    icon: Briefcase,
    color: '#F15A24',
    initiative: 'SEEAL Africa',
    description: 'SEEAL Africa expands economic opportunity by equipping individuals with skills, mentorship, and enterprise development pathways.',
    items: [
      'Skills training programs',
      'Mentorship and guidance',
      'Enterprise development',
      'Income generation support',
    ],
    goal: 'Help translate skills and ideas into sustainable livelihoods.',
  },
  {
    category: 'Behavioral Health Support',
    icon: Shield,
    color: '#6EC1E4',
    initiative: 'BHNA',
    description: 'Behavioral Health Navigation & Workforce Agency helps individuals and families understand and navigate behavioral health systems.',
    items: [
      'System navigation assistance',
      'Community partnerships',
      'Workforce development',
      'Veteran support services',
    ],
    goal: 'Strengthen access to behavioral health resources.',
  },
];

export default function ProgramsPage() {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Hero */}
      <section
        className="relative pt-32 pb-20 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${HERO_BG})` }}
        data-testid="programs-hero"
      >
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#2D3E55]/85 to-[#1a2634]/85"></div>

        <div className="container-custom relative z-10">
          <motion.div 
            className="text-center text-white max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="font-heading text-4xl md:text-5xl font-bold mb-6">
              Programs & Impact
            </h1>
            <p className="font-body text-lg text-white/80">
              His Presence Chapel Inc. advances its mission through programs designed to support 
              individuals and communities facing social, economic, and health challenges.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Programs Grid */}
      <section className="section-padding" data-testid="programs-grid">
        <div className="container-custom">
          <div className="grid gap-12">
            {programs.map((program, index) => (
              <motion.div
                key={program.category}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="overflow-hidden hover:shadow-xl transition-shadow" data-testid={`program-${program.category.toLowerCase().replace(' ', '-')}`}>
                  <div className="grid lg:grid-cols-3">
                    <div 
                      className="p-8 flex flex-col justify-center"
                      style={{ backgroundColor: `${program.color}10` }}
                    >
                      <div 
                        className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6"
                        style={{ backgroundColor: program.color }}
                      >
                        <program.icon size={32} className="text-white" />
                      </div>
                      <h2 className="font-heading text-2xl font-semibold text-[#2D3E55] mb-2">
                        {program.category}
                      </h2>
                      <p className="font-body text-sm" style={{ color: program.color }}>
                        Through {program.initiative}
                      </p>
                    </div>

                    <div className="lg:col-span-2 p-8">
                      <p className="font-body text-slate-600 leading-relaxed mb-6">
                        {program.description}
                      </p>
                      
                      <div className="grid sm:grid-cols-2 gap-4 mb-6">
                        {program.items.map((item, idx) => (
                          <div key={idx} className="flex items-center gap-3">
                            <div 
                              className="w-2 h-2 rounded-full"
                              style={{ backgroundColor: program.color }}
                            ></div>
                            <span className="font-body text-sm text-slate-700">{item}</span>
                          </div>
                        ))}
                      </div>

                      <div 
                        className="p-4 rounded-lg"
                        style={{ backgroundColor: `${program.color}10` }}
                      >
                        <p className="font-body text-sm text-slate-700">
                          <strong>Goal:</strong> {program.goal}
                        </p>
                      </div>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Economic Empowerment Highlight */}
      <section className="section-padding bg-[#F8FAFC]" data-testid="empowerment-highlight">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img 
                src={PROGRAM_IMAGE}
                alt="Women in economic empowerment program"
                className="rounded-2xl shadow-xl w-full h-[400px] object-cover"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#F15A24]/10 rounded-full text-[#F15A24] font-body text-sm font-medium mb-6">
                <GraduationCap size={16} />
                Featured Program
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#2D3E55] mb-6">
                Skills Training & Enterprise Development
              </h2>
              <p className="font-body text-slate-600 leading-relaxed mb-6">
                Our economic empowerment programs focus on equipping individuals with the skills 
                and resources they need to build sustainable livelihoods. From structured training 
                cohorts to mentorship programs, we help transform potential into opportunity.
              </p>
              
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                  <Users className="mx-auto mb-2 text-[#F15A24]" size={28} />
                  <p className="font-heading text-2xl font-bold text-[#2D3E55]">500+</p>
                  <p className="font-body text-xs text-slate-600">Participants Trained</p>
                </div>
                <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                  <Briefcase className="mx-auto mb-2 text-[#F7B500]" size={28} />
                  <p className="font-heading text-2xl font-bold text-[#2D3E55]">150+</p>
                  <p className="font-body text-xs text-slate-600">Enterprises Launched</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}